// ============================================
// THEME CONFIGURATION - TOTALMENTE PARAMETRIZÁVEL
// ============================================

export interface ThemeColors {
  primary: ColorScale;
  accent: ColorScale;
  surface: ColorScale;
  success: string;
  warning: string;
  error: string;
  info: string;
}

export interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950?: string;
}

export interface ThemeConfig {
  name: string;
  colors: ThemeColors;
  borderRadius: string;
  sidebarWidth: string;
  headerHeight: string;
  logo?: string;
  favicon?: string;
}

// Tema padrão - Cyber Cyan
export const defaultTheme: ThemeConfig = {
  name: 'Cyber Cyan',
  colors: {
    primary: {
      50: '236 254 255',
      100: '207 250 254',
      200: '165 243 252',
      300: '103 232 249',
      400: '34 211 238',
      500: '6 182 212',
      600: '8 145 178',
      700: '14 116 144',
      800: '21 94 117',
      900: '22 78 99',
      950: '8 51 68',
    },
    accent: {
      50: '250 245 255',
      100: '243 232 255',
      200: '233 213 255',
      300: '216 180 254',
      400: '192 132 252',
      500: '168 85 247',
      600: '147 51 234',
      700: '126 34 206',
      800: '107 33 168',
      900: '88 28 135',
    },
    surface: {
      50: '248 250 252',
      100: '241 245 249',
      200: '226 232 240',
      300: '203 213 225',
      400: '148 163 184',
      500: '100 116 139',
      600: '71 85 105',
      700: '51 65 85',
      800: '30 41 59',
      900: '15 23 42',
      950: '2 6 23',
    },
    success: '34 197 94',
    warning: '234 179 8',
    error: '239 68 68',
    info: '59 130 246',
  },
  borderRadius: '12px',
  sidebarWidth: '280px',
  headerHeight: '64px',
};

// Tema alternativo - Neon Green
export const neonGreenTheme: ThemeConfig = {
  name: 'Neon Green',
  colors: {
    primary: {
      50: '240 253 244',
      100: '220 252 231',
      200: '187 247 208',
      300: '134 239 172',
      400: '74 222 128',
      500: '34 197 94',
      600: '22 163 74',
      700: '21 128 61',
      800: '22 101 52',
      900: '20 83 45',
      950: '5 46 22',
    },
    accent: {
      50: '254 252 232',
      100: '254 249 195',
      200: '254 240 138',
      300: '253 224 71',
      400: '250 204 21',
      500: '234 179 8',
      600: '202 138 4',
      700: '161 98 7',
      800: '133 77 14',
      900: '113 63 18',
    },
    surface: {
      50: '250 250 250',
      100: '244 244 245',
      200: '228 228 231',
      300: '212 212 216',
      400: '161 161 170',
      500: '113 113 122',
      600: '82 82 91',
      700: '63 63 70',
      800: '39 39 42',
      900: '24 24 27',
      950: '9 9 11',
    },
    success: '34 197 94',
    warning: '234 179 8',
    error: '239 68 68',
    info: '59 130 246',
  },
  borderRadius: '8px',
  sidebarWidth: '260px',
  headerHeight: '60px',
};

// Tema alternativo - Electric Blue
export const electricBlueTheme: ThemeConfig = {
  name: 'Electric Blue',
  colors: {
    primary: {
      50: '239 246 255',
      100: '219 234 254',
      200: '191 219 254',
      300: '147 197 253',
      400: '96 165 250',
      500: '59 130 246',
      600: '37 99 235',
      700: '29 78 216',
      800: '30 64 175',
      900: '30 58 138',
      950: '23 37 84',
    },
    accent: {
      50: '255 241 242',
      100: '255 228 230',
      200: '254 205 211',
      300: '253 164 175',
      400: '251 113 133',
      500: '244 63 94',
      600: '225 29 72',
      700: '190 18 60',
      800: '159 18 57',
      900: '136 19 55',
    },
    surface: {
      50: '248 250 252',
      100: '241 245 249',
      200: '226 232 240',
      300: '203 213 225',
      400: '148 163 184',
      500: '100 116 139',
      600: '71 85 105',
      700: '51 65 85',
      800: '30 41 59',
      900: '15 23 42',
      950: '2 6 23',
    },
    success: '34 197 94',
    warning: '234 179 8',
    error: '239 68 68',
    info: '59 130 246',
  },
  borderRadius: '16px',
  sidebarWidth: '300px',
  headerHeight: '72px',
};

// Função para aplicar tema
export function applyTheme(theme: ThemeConfig): void {
  const root = document.documentElement;
  console.log('[applyTheme] Applying theme:', theme.name);

  // Apply colors
  Object.entries(theme.colors.primary).forEach(([key, value]) => {
    console.log(`[applyTheme] Setting --color-primary-${key} = ${value}`);
    root.style.setProperty(`--color-primary-${key}`, value);
  });

  Object.entries(theme.colors.accent).forEach(([key, value]) => {
    root.style.setProperty(`--color-accent-${key}`, value);
  });

  Object.entries(theme.colors.surface).forEach(([key, value]) => {
    root.style.setProperty(`--color-surface-${key}`, value);
  });

  root.style.setProperty('--color-success', theme.colors.success);
  root.style.setProperty('--color-warning', theme.colors.warning);
  root.style.setProperty('--color-error', theme.colors.error);
  root.style.setProperty('--color-info', theme.colors.info);

  // Apply sizing
  root.style.setProperty('--border-radius', theme.borderRadius);
  root.style.setProperty('--sidebar-width', theme.sidebarWidth);
  root.style.setProperty('--header-height', theme.headerHeight);
  
  console.log('[applyTheme] Theme applied successfully');
}

// Temas disponíveis
export const themes: Record<string, ThemeConfig> = {
  'cyber-cyan': defaultTheme,
  'neon-green': neonGreenTheme,
  'electric-blue': electricBlueTheme,
};
